// @ts-check
/**
 * Adapters between the DashboardAPI Business View overview response and the
 * props the presentational Business View components expect.
 *
 * Keeping the translation here means the components stay prop-driven (and
 * testable against businessViewMockData) while the API shape can change
 * independently.
 *
 * @module businessViewMapper
 */

import { mapSummary } from '../shared/businessViewProjectMapper';

/** @typedef {import('../shared/businessView.types').BusinessViewData} BusinessViewData */
/** @typedef {import('../shared/businessView.types').BusinessViewOverviewResponse} BusinessViewOverviewResponse */

/** Number of days of run trends the API returns (see api.yaml). */
export const TREND_DAYS = 30;

/**
 * Map the API `current` summary onto the KPI row.
 *
 * @param {BusinessViewOverviewResponse['current']} [current]
 * @returns {import('../shared/businessView.types').BusinessViewKpis}
 */
export const mapKpis = (current) => ({
  totalFlows: current?.totalFlows ?? 0,
  atRisk: current?.atRisk ?? 0,
  monitor: current?.monitor ?? 0,
  healthy: current?.healthy ?? 0,
  dailyRiskCost: current?.dailyRiskCost ?? 0,
});

/**
 * Map the API `trends` array onto the stacked bar chart series. Sorted oldest
 * first because the chart reads left to right and slices from the end.
 *
 * @param {BusinessViewOverviewResponse['trends']} [trends]
 * @returns {import('../shared/businessView.types').FlowStatusTrendPoint[]}
 */
export const mapTrend = (trends = []) =>
  [...trends]
    .sort((a, b) => new Date(a?.completedAt ?? 0).getTime() - new Date(b?.completedAt ?? 0).getTime())
    .map((trend) => ({
      run: trend?.runName || `Release ${trend?.releaseId ?? ''}`.trim(),
      completedAt: trend?.completedAt ?? null,
      at_risk: trend?.atRisk ?? 0,
      monitor: trend?.monitor ?? 0,
      healthy: trend?.healthy ?? 0,
      // Left null rather than defaulted to 0 when the run reported no cost: £0 is a
      // real figure (nothing at risk) and must not be invented for a missing one,
      // which would drag the cost trend down towards a value never recorded.
      dailyRiskCost: typeof trend?.dailyRiskCost === 'number' ? trend.dailyRiskCost : null,
    }));

/**
 * The configured per-day impact of a workflow being broken - "Cost At Risk" on the table,
 * "Daily Risk Cost" on the detail view. The two fallback names mean a rename on the API
 * side degrades to £0 rather than NaN.
 *
 * @param {any} [workflow]
 * @returns {number}
 */
export const getWorkflowCostImpact = (workflow) =>
  workflow?.costImpact ?? workflow?.dailyRiskCost ?? workflow?.configuredCostImpact ?? 0;

/**
 * Map the API `workflows` array onto flow table rows.
 *
 * The nulls on an uncalculated workflow are carried through rather than defaulted to 0:
 * "0 of 0 failed" would read as a clean result rather than no result.
 *
 * @param {BusinessViewOverviewResponse['workflows']} [workflows]
 * @returns {import('../shared/businessView.types').FlowRow[]}
 */
export const mapFlows = (workflows = []) =>
  workflows.map((workflow) => ({
    id: String(workflow?.workflowId ?? workflow?.workflowKey ?? ''),
    name: workflow?.name || workflow?.workflowKey || '',
    description: workflow?.description || '',
    status: workflow?.riskStatus ?? 'unavailable',
    categories: workflow?.riskCategories ?? [],
    costAtRisk: getWorkflowCostImpact(workflow),
    failedTests: workflow?.nonPassingTests ?? null,
    totalTests: workflow?.totalTests ?? null,
    calculationStatus: workflow?.calculationStatus ?? null,
    calculationMessage: workflow?.calculationMessage ?? null,
  }));

/**
 * Map a full overview response (plus the selected project) onto the payload the
 * Business View components consume.
 *
 * @param {BusinessViewOverviewResponse|null} [overview]
 * @param {any} [project]
 * @returns {BusinessViewData}
 */
export const mapOverviewToBusinessView = (overview, project) => ({
  summary: mapSummary(project),
  kpis: mapKpis(overview?.current),
  trendDays: TREND_DAYS,
  trend: mapTrend(overview?.trends),
  flows: mapFlows(overview?.workflows),
  calculatedAt: overview?.current?.calculatedAt ?? null,
});

export default mapOverviewToBusinessView;